import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contentResource, collectionSchemas } from './api';
import { getCollection } from '@/config/collections';
import { keys } from '@/lib/query/keys';
import { useAuth } from '@/lib/auth/useAuth';
import { useGate } from '@/lib/rbac/Gate';
import { useApiError } from '@/lib/query/useApiError';
import { useToast } from '@/lib/ui';
import { slugify } from '@/lib/slug';
import { diffPatch } from '@/lib/forms/diff';
import { isLocaleIncomplete } from '@/features/shared/governance';
import { ArabicCompleteControl } from '@/features/shared/ArabicCompleteControl';
import { DataView } from '@/lib/ui/states';
import { PageHeader } from '@/lib/ui/Typography';
import { Tabs } from '@/lib/ui/Tabs';
import { Card } from '@/lib/ui/Card';
import { TextInput } from '@/lib/ui/Field';
import { I18nField } from '@/lib/forms/widgets/I18nField';
import { SchemaForm } from '@/lib/forms/SchemaForm';
import { DeepLinkFocusProvider, FieldFocus } from '@/lib/forms/deepLinkFocus';
import { resolveFocusPath, TITLE_FOCUS_PATH } from './searchFocus';
import { SeoPanel, EMPTY_SEO } from '@/features/shared/SeoPanel';
import { PublishControls } from '@/features/shared/PublishControls';
import { PreviewButton } from '@/features/preview/preview';
import { RevisionsTab } from '@/features/revisions/RevisionsTab';
import { Button, PermissionButton } from '@/lib/ui/Button';
import { ConfirmDialog } from '@/lib/ui/ConfirmDialog';
import type { ContentItem, I18n, SEO } from '@/lib/api/types';

const ENTITY_TYPE: Record<string, string> = {
  industries: 'industry',
  projects: 'project',
  articles: 'article',
  'job-postings': 'job',
  products: 'product',
  locations: 'location',
  subsidiaries: 'subsidiary',
  'site-assets': 'site_asset',
};

export function ContentEditorPage() {
  const { t } = useTranslation();
  const { collection = '', id } = useParams();
  const cfg = getCollection(collection);
  const isNew = id === 'new' || !id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const toast = useToast();
  const onError = useApiError();
  const { isTranslateOnly } = useAuth();
  const res = contentResource(collection);

  // Pre-fill a new product's family linkage when launched from a family editor
  // (`/content/products/new?family=<slug>&family_id=<uuid>`), so the relationship is set
  // without the editor hunting for it.
  const initialDoc = useMemo<Record<string, unknown>>(() => {
    if (!isNew || collection !== 'products') return {};
    const family = searchParams.get('family');
    const familyId = searchParams.get('family_id');
    return family ? { family, ...(familyId ? { family_id: familyId } : {}) } : {};
  }, [isNew, collection, searchParams]);

  const { allowed: canWrite } = useGate('content:write');
  const { allowed: canDeleteGate } = useGate('content:delete');
  // Edit-only collections are a fixed register; deletion is never offered.
  const canDelete = canDeleteGate && !cfg?.editOnly;

  const query = useQuery({
    queryKey: keys.content.detail(collection, id ?? 'new'),
    queryFn: () => res.get(id!),
    enabled: !!cfg && !isNew,
  });

  if (!cfg) return <p className="text-small text-ink-mute">{t('content.unknownCollection')}</p>;

  // These collections are a fixed register — records are edit-only, so creation is disabled.
  if (cfg.editOnly && isNew) return <Navigate to={`/content/${collection}`} replace />;

  if (isNew) {
    return (
      <Editor
        collection={collection}
        cfg={cfg}
        original={null}
        initialDoc={initialDoc}
        canWrite={canWrite}
        canDelete={canDelete}
        translateOnly={isTranslateOnly}
        onSaved={(item) => navigate(`/content/${collection}/${item.id}`)}
        onDeleted={() => navigate(`/content/${collection}`)}
        res={res}
        qc={qc}
        toast={toast}
        onError={onError}
      />
    );
  }

  return (
    <DataView query={query}>
      {(item) => (
        <Editor
          collection={collection}
          cfg={cfg}
          original={item}
          focusQuery={searchParams.get('q')}
          canWrite={canWrite}
          canDelete={canDelete}
          translateOnly={isTranslateOnly}
          onSaved={() => qc.invalidateQueries({ queryKey: keys.content.detail(collection, item.id) })}
          onDeleted={() => navigate(`/content/${collection}`)}
          res={res}
          qc={qc}
          toast={toast}
          onError={onError}
        />
      )}
    </DataView>
  );
}

type EditorProps = {
  collection: string;
  cfg: NonNullable<ReturnType<typeof getCollection>>;
  original: ContentItem | null;
  initialDoc?: Record<string, unknown>;
  /** Global-search term (`?q=`) to scroll to + highlight the matched field, when arriving from search. */
  focusQuery?: string | null;
  canWrite: boolean;
  canDelete: boolean;
  translateOnly: boolean;
  onSaved: (item: ContentItem) => void;
  onDeleted: () => void;
  res: ReturnType<typeof contentResource>;
  qc: ReturnType<typeof useQueryClient>;
  toast: ReturnType<typeof useToast>;
  onError: ReturnType<typeof useApiError>;
};

function Editor({
  collection,
  cfg,
  original,
  initialDoc,
  focusQuery,
  canWrite,
  canDelete,
  translateOnly,
  onSaved,
  onDeleted,
  res,
  qc,
  toast,
  onError,
}: EditorProps) {
  const { t } = useTranslation();
  const isNew = !original;
  const [title, setTitle] = useState<I18n>(original?.title ?? { en: '', ar: '' });
  const [ident, setIdent] = useState(original?.slug || original?.key || '');
  const [identTouched, setIdentTouched] = useState(!!original);
  const [sortOrder, setSortOrder] = useState(original?.sort_order ?? 0);
  const [seo, setSeo] = useState<SEO>(original?.seo ?? EMPTY_SEO);
  const [doc, setDoc] = useState<Record<string, unknown>>(
    (original?.doc as Record<string, unknown>) ?? initialDoc ?? {},
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Arabic-completeness flag lives in `doc.ar_complete` (the publish gate keys off it, §4.3).
  const arComplete = doc.ar_complete !== false; // absent ⇒ treated complete (legacy-safe)
  const setArComplete = (v: boolean) => setDoc((d) => ({ ...d, ar_complete: v }));
  const coverageOf = useMemo(() => ({ title, doc }), [title, doc]);

  const entityType = ENTITY_TYPE[collection] ?? collection;

  // Arriving from global search: resolve the searched term to the doc/title field that matched
  // so it scrolls into view and pulses (the API gives no field info for content hits, so we
  // derive it from the loaded record). Computed from the original record, not live edits.
  const focusPath = useMemo(
    () => resolveFocusPath(original?.title, original?.doc, focusQuery),
    [original, focusQuery],
  );

  // The `doc` form is generated from the backend's canonical schema for this entity
  // (industry/project/article/job) so the editor, server validation, and the public
  // site share one shape. Collections the backend doesn't model yet (products,
  // locations, subsidiaries) fall back to the static registry schema.
  const schemaQuery = useQuery({
    queryKey: keys.collectionSchemas(),
    queryFn: () => collectionSchemas(),
    staleTime: 60 * 60 * 1000,
  });
  const docSchema = schemaQuery.data?.[entityType] ?? cfg.docSchema;

  const effectiveIdent = useMemo(
    () => (cfg.identType === 'slug' && !identTouched ? slugify(title.en) : ident),
    [cfg.identType, identTouched, title.en, ident],
  );

  const buildBody = () => {
    const body: Record<string, unknown> = {
      title,
      sort_order: sortOrder,
      doc,
    };
    if (cfg.identType === 'slug') body.slug = effectiveIdent;
    else body.key = effectiveIdent;
    if (cfg.hasSeo) body.seo = seo;
    return body;
  };

  const save = useMutation({
    mutationFn: async () => {
      setErrors({});
      if (isNew) return res.create(buildBody());
      // Partial diff against the loaded server state (spec §5.5).
      const next = buildBody();
      const prev: Record<string, unknown> = {
        title: original.title,
        sort_order: original.sort_order,
        doc: original.doc,
        ...(cfg.identType === 'slug' ? { slug: original.slug } : { key: original.key }),
        ...(cfg.hasSeo ? { seo: original.seo } : {}),
      };
      return res.update(original.id, diffPatch(prev, next));
    },
    onSuccess: (item) => {
      void qc.invalidateQueries({ queryKey: keys.content.all(collection) });
      void qc.invalidateQueries({ queryKey: keys.dashboard() });
      toast.success(isNew ? t('content.createdToast') : t('content.savedToast'), t('content.siteUpdateBody'));
      onSaved(item);
    },
    onError: (e) => onError(e, { onValidation: setErrors }),
  });

  const publish = useMutation({
    mutationFn: (scheduledAt?: string) => res.publish(original!.id, scheduledAt),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.content.all(collection) });
      toast.success(t('content.publicationUpdated'), t('content.siteUpdateBody'));
    },
    onError: (e) => {
      // The Arabic-copy gate (§4.3) blocks publish when `doc.ar_complete` is false — give a
      // specific reason instead of a generic validation toast.
      if (isLocaleIncomplete(e)) {
        toast.error(t('governance.publishBlockedAr'), { description: t('governance.publishBlockedArDetail') });
        return;
      }
      onError(e);
    },
  });
  const unpublish = useMutation({
    mutationFn: () => res.unpublish(original!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.content.all(collection) });
      toast.success(
        t('content.unpublishedToast'),
        cfg.identType === 'slug' ? t('content.unpublishRedirect') : undefined,
      );
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: () => res.remove(original!.id),
    onSuccess: () => {
      // Drop the now-deleted detail from the cache so it can't refetch into a 404 before
      // the route unmounts (FE-11).
      qc.removeQueries({ queryKey: keys.content.detail(collection, original!.id) });
      void qc.invalidateQueries({ queryKey: keys.content.all(collection) });
      void qc.invalidateQueries({ queryKey: keys.dashboard() });
      setConfirmDelete(false);
      onDeleted();
    },
    onError,
  });

  const identityForm = (
    <Card>
      <div className="flex flex-col gap-md">
        <FieldFocus path={TITLE_FOCUS_PATH}>
          <I18nField
            label={t('common.title')}
            value={title}
            onChange={setTitle}
            required
            error={errors['title.en'] ?? errors.title}
            translateOnly={translateOnly}
          />
        </FieldFocus>
        <TextInput
          label={cfg.identType === 'slug' ? t('common.slug') : t('common.key')}
          required={cfg.identType === 'key'}
          value={effectiveIdent}
          disabled={translateOnly}
          onChange={(e) => {
            setIdentTouched(true);
            setIdent(e.target.value);
          }}
          error={errors.slug ?? errors.key}
          hint={cfg.identType === 'slug' ? t('content.slugHint') : undefined}
        />
        <TextInput
          type="number"
          label={t('content.sortOrder')}
          value={String(sortOrder)}
          disabled={translateOnly}
          onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
        />
      </div>
    </Card>
  );

  const docForm = (
    <Card>
      {schemaQuery.isLoading ? (
        <p className="text-small text-ink-mute">{t('common.loading', { defaultValue: 'Loading…' })}</p>
      ) : (
        <SchemaForm
          schema={docSchema}
          value={doc}
          onChange={setDoc}
          translateOnly={translateOnly}
          errors={errors}
        />
      )}
    </Card>
  );

  const arForm = (
    <Card>
      <ArabicCompleteControl coverageOf={coverageOf} checked={arComplete} onChange={setArComplete} disabled={translateOnly} />
    </Card>
  );

  const tabs = [
    { value: 'content', label: t('content.contentTab'), content: <DeepLinkFocusProvider path={focusPath}><div className="flex flex-col gap-lg">{identityForm}{docForm}{arForm}</div></DeepLinkFocusProvider> },
    ...(cfg.hasSeo
      ? [{ value: 'seo', label: t('content.seoTab'), content: <Card><SeoPanel value={seo} onChange={setSeo} translateOnly={translateOnly} /></Card> }]
      : []),
    ...(!isNew
      ? [{ value: 'history', label: t('content.historyTab'), content: <RevisionsTab entityType={entityType} entityId={original.id} /> }]
      : []),
  ];

  const collectionLabel = t(cfg.label, { defaultValue: cfg.label });
  // Editor heading uses the singular ("New product"), not the plural collection label.
  const singularLabel = t(cfg.singularLabel, { defaultValue: cfg.singularLabel });

  return (
    <div>
      <PageHeader
        eyebrow={collectionLabel.toUpperCase()}
        title={isNew ? t('content.newTitle', { label: singularLabel }) : title.en || t('content.untitled')}
        actions={original ? <PreviewButton entityType={entityType} entityId={original.id} /> : undefined}
      />

      {original && (
        <div className="mb-lg flex flex-wrap items-center justify-between gap-md">
          <PublishControls
            status={original.status}
            scheduledAt={original.scheduled_at}
            onPublish={(at) => publish.mutate(at)}
            onUnpublish={() => unpublish.mutate(undefined)}
            busy={publish.isPending || unpublish.isPending}
          />
          {canDelete && (
            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}>
              {t('common.delete')}
            </Button>
          )}
        </div>
      )}

      <Tabs tabs={tabs} />

      {/* Sticky action bar so Save stays reachable while editing a long form. */}
      <div className="sticky bottom-0 z-20 -mx-lg mt-lg flex items-center justify-end gap-sm border-t-[0.5px] border-hairline bg-canvas-sand/90 px-lg py-md backdrop-blur lg:-mx-xl lg:px-xl">
        <PermissionButton
          allowed={canWrite || translateOnly}
          reason={t('content.editPermission')}
          loading={save.isPending}
          onClick={() => save.mutate(undefined)}
        >
          {isNew ? t('common.saveDraft') : t('common.save')}
        </PermissionButton>
      </div>

      {original && (
        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title={t('content.deleteItemTitle')}
          description={t('content.deleteItemDescription')}
          confirmLabel={t('common.delete')}
          tone="danger"
          loading={remove.isPending}
          onConfirm={() => remove.mutate(undefined)}
        />
      )}
    </div>
  );
}
